// src/utils/scoring.ts
import { Question, TraitLevel, TraitScore, SectionScore } from "../types";

/** 
 * Scoring Engine - Universal across all exam types
 * Handles Likert (behavioral) and MCQ (aptitude) scoring
 */

// ============================================================================
// Core Scoring Functions
// ============================================================================

/**
 * Compute score for a single behavioral (Likert) question response
 * @param answer - User's answer (1-5 or 1-7 scale)
 * @param weightage - Question weightage multiplier (default: 1)
 * @param isReverse - Whether the question is reverse scored
 * @param maxScale - Maximum scale value (default: 5)
 * @returns Calculated score
 */
export function calcBehavioralScore(
  answer: number,
  weightage: number = 1,
  isReverse: boolean = false,
  maxScale: number = 5
): number {
  if (answer === undefined || answer === null) return 0;
  let score = isReverse ? (maxScale + 1 - answer) : answer;
  return score * weightage;
}

/**
 * Compute score for a single aptitude (MCQ) question response
 * @param selectedOptionId - ID of the selected option
 * @param question - Question object with options and weightage
 * @returns Score (weightage if correct, 0 otherwise)
 */
export function calcAptitudeScore(
  selectedOptionId: string | undefined,
  question: Question
): number {
  if (!selectedOptionId) return 0;
  const correct = question.options?.find(o => o.isCorrect);
  if (correct && selectedOptionId === correct.id) return question.weightage || 1;
  return 0;
}

/**
 * Generic MCQ scoring (works with partial question object)
 */
export function calcMcqScore(
  question: { options?: { id: string; isCorrect?: boolean }[]; weightage?: number },
  selectedOptionId?: string
): number {
  if (!selectedOptionId) return 0;
  const correct = question.options?.find(o => o.isCorrect);
  if (correct && selectedOptionId === correct.id) return question.weightage || 1;
  return 0;
}

// ============================================================================
// Trait Level Classification
// ============================================================================

/**
 * Classify a trait percentage into Low / Moderate / High
 * @param percentage - Score percentage (0-100)
 * @returns Trait level
 */
export function getTraitLevel(percentage: number): TraitLevel {
  if (percentage <= 40) return "Low";
  if (percentage <= 70) return "Moderate";
  return "High";
}

/**
 * Returns a color hex for the given trait level
 * @param level - Trait level
 * @returns Hex color code
 */
export function traitLevelColor(level: TraitLevel): string {
  if (level === "High") return "#10b981";      // Green
  if (level === "Moderate") return "#f59e0b";  // Amber
  return "#ef4444";                             // Red
}

// ============================================================================
// Trait Score Aggregation
// ============================================================================

/**
 * Aggregate trait scores from a set of questions + user answers
 * @param questions - Array of questions to aggregate
 * @param answers - User answers mapping (questionId -> answer)
 * @param traitId - ID of the trait to calculate
 * @param traitName - Name of the trait
 * @returns TraitScore object
 */
export function aggregateTraitScores(
  questions: Question[],
  answers: Record<string, number | string>,
  traitId: string,
  traitName: string
): TraitScore {
  const qs = questions.filter(q => q.traitId === traitId);
  let score = 0;
  let maxScore = 0;
  
  for (const q of qs) {
    const val = answers[q.id];
    if (val !== undefined && val !== null) {
      if (q.type === "likert") {
        score += calcBehavioralScore(val as number, q.weightage, q.isReverse, q.maxScale);
        maxScore += (q.maxScale || 5) * (q.weightage || 1);
      } else if (q.type === "mcq") {
        const mcqScore = calcAptitudeScore(val as string, q);
        score += mcqScore;
        maxScore += q.weightage || 1;
      } else {
        maxScore += q.weightage || 1;
      }
    } else {
      // No answer provided - still count max possible score
      if (q.type === "likert") {
        maxScore += (q.maxScale || 5) * (q.weightage || 1);
      } else {
        maxScore += q.weightage || 1;
      }
    }
  }
  
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  return {
    traitId,
    traitName,
    score,
    maxScore,
    percentage,
    level: getTraitLevel(percentage)
  };
}

/**
 * Calculate trait score from questions and answers (simplified version)
 */
export function calculateTraitScore(
  traitId: string,
  traitName: string,
  questions: any[],
  answers: Record<string, any>
): TraitScore {
  let score = 0;
  let maxScore = 0;
  
  questions.forEach(q => {
    const val = answers[q.id];
    if (val !== undefined && val !== null) {
      if (q.type === "likert") {
        score += calcBehavioralScore(val, q.weightage, q.isReverse, q.maxScale);
        maxScore += (q.maxScale || 5) * (q.weightage || 1);
      } else if (q.type === "mcq") {
        const mcqScore = calcMcqScore(q, val);
        score += mcqScore;
        maxScore += q.weightage || 1;
      } else {
        maxScore += q.weightage || 1;
      }
    } else {
      maxScore += q.type === "likert" ? (q.maxScale || 5) * (q.weightage || 1) : (q.weightage || 1);
    }
  });
  
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  return {
    traitId,
    traitName,
    score,
    maxScore,
    percentage,
    level: getTraitLevel(percentage)
  };
}

// ============================================================================
// Section Score Aggregation
// ============================================================================

/**
 * Aggregate section scores from a set of questions + user answers
 * @param questions - Array of questions to aggregate
 * @param answers - User answers mapping (questionId -> answer)
 * @param sectionId - ID of the section to calculate
 * @param sectionName - Name of the section
 * @returns SectionScore object
 */
export function aggregateSectionScore(
  questions: Question[],
  answers: Record<string, string | number>,
  sectionId: string,
  sectionName: string
): SectionScore {
  const qs = questions.filter(q => q.sectionId === sectionId);
  let score = 0;
  let maxScore = 0;
  
  for (const q of qs) {
    const val = answers[q.id];
    if (val !== undefined && val !== null) {
      if (q.type === "likert") {
        score += calcBehavioralScore(val as number, q.weightage, q.isReverse, q.maxScale);
        maxScore += (q.maxScale || 5) * (q.weightage || 1);
      } else if (q.type === "mcq") {
        const mcqScore = calcAptitudeScore(val as string, q);
        score += mcqScore;
        maxScore += q.weightage || 1;
      } else {
        maxScore += q.weightage || 1;
      }
    } else {
      if (q.type === "likert") {
        maxScore += (q.maxScale || 5) * (q.weightage || 1);
      } else {
        maxScore += q.weightage || 1;
      }
    }
  }
  
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  return {
    sectionId,
    sectionName,
    score,
    maxScore,
    percentage
  };
}

/**
 * Calculate section score from questions and answers (simplified version)
 */
export function calculateSectionScore(
  sectionId: string,
  sectionName: string,
  questions: any[],
  answers: Record<string, any>
): SectionScore {
  let score = 0;
  let maxScore = 0;
  
  questions.forEach(q => {
    const val = answers[q.id];
    if (val !== undefined && val !== null) {
      if (q.type === "likert") {
        score += calcBehavioralScore(val, q.weightage, q.isReverse, q.maxScale);
        maxScore += (q.maxScale || 5) * (q.weightage || 1);
      } else if (q.type === "mcq") {
        const mcqScore = calcMcqScore(q, val);
        score += mcqScore;
        maxScore += q.weightage || 1;
      } else {
        maxScore += q.weightage || 1;
      }
    } else {
      maxScore += q.type === "likert" ? (q.maxScale || 5) * (q.weightage || 1) : (q.weightage || 1);
    }
  });
  
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  return {
    sectionId,
    sectionName,
    score,
    maxScore,
    percentage
  };
}

// ============================================================================
// Overall Score Calculation
// ============================================================================

/**
 * Calculate overall exam result
 * @param questions - All questions in the exam
 * @param answers - User answers
 * @returns Total score, max score, and percentage
 */
export function calculateOverallScore(
  questions: Question[],
  answers: Record<string, any>
): { score: number; maxScore: number; percentage: number } {
  let score = 0;
  let maxScore = 0;
  
  for (const q of questions) {
    const val = answers[q.id];
    if (val !== undefined && val !== null) {
      if (q.type === "likert") {
        score += calcBehavioralScore(val, q.weightage, q.isReverse, q.maxScale);
        maxScore += (q.maxScale || 5) * (q.weightage || 1);
      } else if (q.type === "mcq") {
        const mcqScore = calcAptitudeScore(val, q);
        score += mcqScore;
        maxScore += q.weightage || 1;
      } else {
        maxScore += q.weightage || 1;
      }
    } else {
      if (q.type === "likert") {
        maxScore += (q.maxScale || 5) * (q.weightage || 1);
      } else {
        maxScore += q.weightage || 1;
      }
    }
  }
  
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  return { score, maxScore, percentage };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get score interpretation based on percentage
 */
export function getScoreInterpretation(percentage: number): {
  level: "Excellent" | "Good" | "Average" | "Poor";
  color: string;
  message: string;
} {
  if (percentage >= 85) {
    return {
      level: "Excellent",
      color: "#10b981",
      message: "Outstanding performance! You've demonstrated exceptional understanding."
    };
  } else if (percentage >= 70) {
    return {
      level: "Good",
      color: "#3b82f6",
      message: "Good performance! You have a solid understanding of the concepts."
    };
  } else if (percentage >= 50) {
    return {
      level: "Average",
      color: "#f59e0b",
      message: "Average performance. There's room for improvement in some areas."
    };
  } else {
    return {
      level: "Poor",
      color: "#ef4444",
      message: "Below average performance. Consider reviewing the material and trying again."
    };
  }
}

/**
 * Calculate weighted score with custom weights
 */
export function calculateWeightedScore(
  scores: { score: number; maxScore: number; weight: number }[]
): { score: number; maxScore: number; percentage: number } {
  let totalWeightedScore = 0;
  let totalWeightedMax = 0;
  let totalWeight = 0;
  
  for (const item of scores) {
    totalWeightedScore += (item.score / item.maxScore) * item.weight * 100;
    totalWeightedMax += item.weight * 100;
    totalWeight += item.weight;
  }
  
  const percentage = totalWeightedMax > 0 
    ? Math.round((totalWeightedScore / totalWeightedMax) * 100)
    : 0;
  
  return {
    score: Math.round(totalWeightedScore),
    maxScore: totalWeightedMax,
    percentage
  };
}